import moment from "moment";
import submit from "submit-form";
import {
  convertUnit,
  getBodywweightAndUnit,
  getLoadAndUnit,
} from "./converters/convert-units";
import { simpleHash } from "./string-helpers";
import { formatVolume } from "./volume-helpers";

//  Function to get the modifiers for "react-day-picker", which represent the date range occupied by events
export const getModifiers = (calendarEvents) => {
  const toSort = calendarEvents.map((x) => x);

  //   We sort the array to get the shorter events prevailing over the larger ones
  //   So this is just a sort putting the shortest events at the end, so they get processed last
  //   See Array.sort() docs for a better understanding
  toSort?.sort((eventA, eventB) => {
    if (!eventA.toDate) return 1;
    if (!eventB.toDate) return -1;

    const eventADays = moment(eventA.toDate, "YYYYMMDD").diff(
      moment(eventA.fromDate, "YYYYMMDD"),
      "days"
    );
    const eventBDays = moment(eventB.toDate, "YYYYMMDD").diff(
      moment(eventB.fromDate, "YYYYMMDD"),
      "days"
    );
    if (eventADays < eventBDays) return 1;
    if (eventADays > eventBDays) return -1;
    if (eventADays === eventBDays) return 0;
  });

  const modifiers = toSort.reduce(
    //   accumulator will be an object like the following one, storing the modifiers information
    //       {
    //       modifiers: {},
    //       modifiersStyles: {},
    //       modifiersClassNames: {},
    //     }
    (accumulator, currentEvent) => {
      // Event name replacing " " with "-"
      const name = currentEvent.name.replace(/\s/g, "-").toLowerCase();

      // Check if the current event is between another event's date range
      const isBetweenAnother = calendarEvents.filter(
        (event) =>
          currentEvent.toDate &&
          event.toDate &&
          moment(currentEvent.fromDate).isBetween(
            moment(event.fromDate),
            moment(event.toDate)
          )
      ).length;

      // Check if the date is a range or individual and create its corresponding modifier
      // These are just objects like: { [eventName]: from: Date, to: Date }
      //                          or: { [eventName]: Date } if it is not a range
      const modifiers = currentEvent.toDate
        ? // If it is a range
          {
            ...accumulator.modifiers,
            [`${name}-top`]: moment(currentEvent.fromDate, "YYYYMMDD").toDate(),
            [`${name}-middle`]: [
              {
                from: moment(currentEvent.fromDate, "YYYYMMDD")
                  .add(1, "days")
                  .toDate(),
                to: moment(currentEvent.toDate, "YYYYMMDD")
                  .subtract(1, "days")
                  .toDate(),
              },
            ],
            [`${name}-bottom`]: moment(
              currentEvent.toDate,
              "YYYYMMDD"
            ).toDate(),
          }
        : // If it is just an individual date
          {
            ...accumulator.modifiers,
            [name]: moment(currentEvent.fromDate, "YYYYMMDD").toDate(),
          };
      // This color property makes contrast with the background color of the event (black | white)
      const color = invertColor(currentEvent.eventColor);

      // Same thing than modifiers, but here we apply styles
      // ["*-top"] / ["*-bottom"] properties modify the rounded corners of the starting/ending dates of ranges
      const modifiersStyles = currentEvent.toDate
        ? {
            ...accumulator.modifiersStyles,
            [`${name}-middle`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: 0,
              transition: "all .5s",
            },
            [`${name}-top`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: `${isBetweenAnother ? "0 0" : "100% 100%"} 0 0`,
              transition: "all .5s",
            },
            [`${name}-bottom`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: `0 0 ${isBetweenAnother ? "0 0" : "100% 100%"}`,
              transition: "all .5s",
            },
          }
        : // Just and individual date styles
          {
            ...accumulator.modifiersStyles,
            [name]: {
              color: color,
              backgroundColor: `${currentEvent.eventColor}`,
              transition: "all .5s",
            },
          };

      // Also getting some classnames to modify through css
      const modifiersClassNames = currentEvent.toDate
        ? {
            ...accumulator.modifiersClassNames,
            [`${name}-middle`]: `calendar-custom-modifier middle ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
            [`${name}-top`]: `calendar-custom-modifier top ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
            [`${name}-bottom`]: `calendar-custom-modifier bottom ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
          }
        : {
            ...accumulator.modifiersClassNames,
            [`${name}`]: `calendar-custom-modifier single ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
          };

      return { modifiers, modifiersStyles, modifiersClassNames };
    },
    // Initial state of the accumulator
    {
      modifiers: {},
      modifiersStyles: {},
      modifiersClassNames: {},
    }
  );

  return modifiers;
};

//  Function to determinate if the text color should be black or white
export const invertColor = (hex, forFilter = false) => {
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }

  const r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);

  const lum = r * 0.299 + g * 0.587 + b * 0.114;
  const isLight = lum > 150;

  if (forFilter) {
    return isLight ? "var(--black-filter)" : "var(--filter-white)";
  }
  return isLight ? "var(--black-always)" : "var(--white-always)";
};

// Phase/milestone pills (PhasePill, MilestonePill) show the coach's raw
// custom color as TEXT over a faint 16%-tinted version of that same color
// (see .phase-pill/.summary-pill.colored, annual-planner.css) - a duotone
// look, not a solid chip, so invertColor's flat black/white swap doesn't fit
// here (it'd replace the color entirely instead of just making it legible).
// In dark theme, that tinted background sits close to the page's own dark
// background, so a naturally dark custom color (e.g. a deep green) ends up
// as dark text on an almost-equally-dark background - unreadable. Light
// theme has the mirror problem: the same tint sits on a near-white page, so
// a naturally pale custom color (e.g. a light pastel) ends up just as hard
// to read there.
//
// Same luminance formula as invertColor, but instead of a hard black/white
// swap this blends proportionally toward the theme's own background corner
// (white for dark theme, black for light theme) only as much as each color
// actually needs - one already on the readable side of the line comes back
// untouched, an extreme one gets pulled further. `mixToward` is the
// luminance of that corner (255/0), which conveniently also is the formula's
// only difference between the two directions: luminance is linear in RGB
// under this weighted sum (weights sum to 1), so solving
// (1-t)*lum + t*mixToward = targetLum for t gives the exact mix ratio
// needed to land precisely on the limit, no iteration required.
const clampPhaseColorLuminance = (hex, targetLum, mixToward) => {
  if (!hex) return hex;
  const clean = hex.indexOf("#") === 0 ? hex.slice(1) : hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return hex;

  const lum = r * 0.299 + g * 0.587 + b * 0.114;
  const needsClamp = mixToward === 255 ? lum < targetLum : lum > targetLum;
  if (!needsClamp) return hex;

  const t = (targetLum - lum) / (mixToward - lum);
  const mix = (channel) => Math.round(channel + t * (mixToward - channel));
  const toHex = (channel) => channel.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

export const readablePhaseColorOnDark = (hex) =>
  clampPhaseColorLuminance(hex, 180, 255);

export const readablePhaseColorOnLight = (hex) =>
  clampPhaseColorLuminance(hex, 150, 0);

/** Keys aligned with moment `day()` (0 = Sunday … 6 = Saturday). */
const ANNUAL_PLANNER_WEEK_DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// Function to sort the annual planner calendar header by the coach first day of the week preference
export const getSortedWeekDays = (startingCalendarDay, t) => {
  const weekDays = ANNUAL_PLANNER_WEEK_DAY_KEYS.map((key, index) => ({
    day: t(`annualPlanner.weekDays.${key}`),
    index,
  }));

  while (weekDays[0].index < startingCalendarDay) {
    weekDays.push(weekDays.shift());
  }

  return weekDays;
};

// Whitespace-only counts as empty for any required text field - trimmed once
// here so every annual planner form (phase names in usePhaseTemplateForm.js
// and the individual-phase modals, event name/description in
// add-event-modal.js) checks it the same way, instead of each repeating (and
// risking forgetting) its own `.trim()`.
export const isBlank = (value) => !value?.trim();

// Closes whatever annual planner modal is open, the same way in every modal
// that offers a plain Cancel (no local "back to read mode" step of its own) -
// extracted after add-event-modal.js and add-individial-phase-modal.js were
// found carrying an identical copy of this submit call.
export const closeModal = () =>
  submit("/actions/refresh?", {
    method: "GET",
    body: {
      "modal[type]": "",
    },
  });

// Every annual planner create/update/delete action goes through this exact
// sequence: fold in organizationCoachId when present, stamp a content-hash
// requestId (see ensureSingleExecution.js, the action-layer middleware that
// dedupes on it) so a double-click doesn't fire the same write twice, then
// POST. Extracted after add-event-modal.js, add-individial-phase-modal.js and
// edit-individual-phase-modal.js were all found carrying their own copy of
// this exact triad.
export const submitAnnualPlannerAction = (url, body, organizationCoachId) => {
  const fullBody = organizationCoachId
    ? { ...body, organizationCoachId }
    : body;
  fullBody.requestId = simpleHash(JSON.stringify(fullBody));
  submit(url, { method: "POST", body: fullBody });
};

// Function to get random color for the add event modal
export const getRandomColor = () =>
  `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, 0)}`;

// Also a function to get random color for the add event modal, but pastel colors
export const getRandomPastelColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = 80;
  const l = 65;

  const hDecimal = l / 100;
  const a = (s * Math.min(hDecimal, 1 - hDecimal)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = hDecimal - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

    // Convert to Hex and prefix with "0" if required
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// This function is used to serve the hidden days for the react-day-picker
// It is used to calculate the days that are hidden in the calendar
// It is used to calculate the number of weeks that we will display in the current month
export const getColumnMonthData = (month, startingCalendarDay = 0) => {
  const lastMonthDay = moment(month).endOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const endAmount = getStartingCalendarDayAdjustment(
    lastMonthDay,
    startingCalendarDay
  );

  const lastWeekDay = moment(lastMonthDay).endOf("week");

  // Here we decide if we have to hide or not the last week of the month
  // This happens when the following month starts in the last week of the current one
  // So we always want to have the first day of the month in the first week of the month;
  // not in the last week of the previous month
  const hiddenDays =
    // If the last day of the last week of the month belongs to the following month, then we have to hide that week
    moment(lastWeekDay).add(endAmount, "days").month() !== moment(month).month()
      ? [
          {
            from: moment(lastWeekDay)
              .startOf("week")
              .add(endAmount, "days")
              .toDate(),
            to: moment(lastWeekDay).add(endAmount, "days").toDate(),
          },
        ]
      : [];

  // Also getting the first day of the month, and the days amount necessary for the starting calendar day adjustment
  const firstMonthDay = moment(month).startOf("month");
  const startAmout = getStartingCalendarDayAdjustment(
    firstMonthDay,
    startingCalendarDay
  );

  // Here we get the number of days of the current month. We subtract 7 if there is a hidden week
  const numberOfDays = hiddenDays.length
    ? moment(lastWeekDay)
        .add(endAmount, "days")
        .diff(firstMonthDay.startOf("week").add(startAmout, "days"), "days") +
      1 -
      7
    : moment(lastWeekDay)
        .add(endAmount, "days")
        .diff(firstMonthDay.startOf("week").add(startAmout, "days"), "days") +
      1;

  // Dividing the amount of days by 7 to get the number of weeks
  const numberOfWeeks = numberOfDays / 7;
  return [numberOfWeeks, hiddenDays];
};

// Function to get the first day of the passed week
export const getWeekColumnData = (month, startingCalendarDay, weekNumber) => {
  const weekFirstDay = moment(month).startOf("month");

  // Amount of days needed for the starting calendar day adjustment
  const amount = getStartingCalendarDayAdjustment(
    weekFirstDay,
    startingCalendarDay
  );

  weekFirstDay.startOf("week").add(amount, "days").add(weekNumber, "weeks");
  return weekFirstDay;
};

// Whether `weekFirstDay` (a Monday-or-whatever-the-coach's-starting-day-is)
// is the week containing today - the grid's "current week" vertical divider
// (see ~/.claude/plans/periodization-calendar-view-redesign.md Fase 4) marks
// this same week's column consistently across every row (phases, notes,
// milestones, stats), each of which already computes its own weekFirstDay
// via getWeekColumnData.
export const isCurrentWeek = (weekFirstDay) =>
  moment
    .utc()
    .isBetween(
      moment(weekFirstDay).startOf("day"),
      moment(weekFirstDay).add(6, "days").endOf("day"),
      undefined,
      "[]"
    );

// Tool for getting the amount of days needed to adjust the starting calendar day for momentjs dates
export const getStartingCalendarDayAdjustment = (
  dayReference,
  startingCalendarDay
) => {
  if (startingCalendarDay <= 0) return 0;
  if (moment(dayReference).day() < startingCalendarDay) {
    return -(7 - startingCalendarDay);
  }
  return startingCalendarDay;
};

// The 12-month grid's exact week boundaries (first week's start day + total
// week count) - extracted so every consumer that needs to walk the same
// weeks (List view's table, the week stepper) agrees on where week 0 starts,
// instead of each re-deriving this from dateFromState independently.
export const getWeekRange = (dateFromState, startingCalendarDay) => {
  const rangeFirstDay = moment(dateFromState).startOf("month");
  const startAmout = getStartingCalendarDayAdjustment(
    rangeFirstDay,
    startingCalendarDay
  );
  rangeFirstDay.startOf("week").add(startAmout, "days");

  const rangeLastDay = moment(dateFromState).add(11, "months").endOf("month");
  const endAmout = getStartingCalendarDayAdjustment(
    rangeLastDay,
    startingCalendarDay
  );
  rangeLastDay.endOf("week").add(endAmout, "days");

  const numberOfWeeks =
    moment(rangeLastDay).diff(moment(rangeFirstDay), "weeks") + 1;

  return { rangeFirstDay, numberOfWeeks };
};

// The 12 months' own week-columns CalendarView.js actually renders, one
// entry per month. NOT the same total as getWeekRange's numberOfWeeks above:
// getColumnMonthData hides a month's trailing week whenever it spills into
// the next month (so the next month's grid column doesn't start partway
// through an already-shown week), while getWeekRange computes one continuous
// day-diff across the full 12 months with no such per-month truncation. The
// two can disagree by a week or more depending on where month boundaries and
// `startingCalendarDay` land, which is why useWeekSelection's clamp - meant
// to mean "the last/first WEEK THE CALENDAR GRID VISIBLY SHOWS" - needs this
// one, not getWeekRange's (sc-47659 QA follow-up: the selected week could
// clamp to an index getWeekRange considered valid but the grid never
// rendered, leaving the Notes panel pointed at a week with nothing
// highlighted). List view keeps using getWeekRange directly - its own table
// has no such per-month boundary to collide with.
export const getMonthsWeekData = (dateFromState, startingCalendarDay) =>
  Array.from({ length: 12 }, (_, i) =>
    moment(dateFromState).add(i, "months")
  ).map((month) => ({
    month,
    numberOfWeeks: getColumnMonthData(month, startingCalendarDay)[0],
  }));

// Getting the default template phases
export const getDefaultPhases = (phasesTemplates) => {
  const defaultPhases =
    phasesTemplates?.templates?.find(
      (template) => template._id === phasesTemplates.defaultTemplate
    ) || [];

  if (typeof defaultPhases === "object") return defaultPhases.phases;
  return defaultPhases;
};

// Resolves which template is currently applied to an athlete and which one
// is the coach's default - phase-templates-modal.js and the Annual
// Planner's "apply a phase template" dropdown both need these same two
// facts, kept in one place instead of re-derived per caller.
export const getPhaseTemplateStatus = ({
  athlete,
  templates,
  defaultTemplate,
}) => {
  const { phaseTemplateId: athleteTemplateId, phases = [] } =
    athlete?.annualPlan?.trainingPhases || {};

  // An athlete with no phases yet has nothing of their own applied - fall
  // back to the coach's default so a fresh athlete still shows one as
  // "current".
  const currentTemplateId =
    athleteTemplateId || (!phases.length && defaultTemplate) || undefined;

  const currentTemplate = templates.find(
    (template) => template._id === currentTemplateId
  );
  const defaultTemplateObject = templates.find(
    (template) => template._id === defaultTemplate
  );

  const isCurrentTheDefault =
    !!currentTemplateId && currentTemplateId === defaultTemplate;

  return {
    currentTemplateId,
    currentTemplate,
    defaultTemplateObject,
    isCurrentTheDefault,
  };
};

// All milestones (List view's Milestones column + PhaseSummary's "next"
// chip) per the Periodization PRD's Goals/Milestones rule: a goal only
// enters the timeline if it has a date - bodyWeight and personalRecord goals
// qualify (undated steps/sleep/waterIntake/custom never do, see
// resolveDailyTargetGoals/resolveCustomGoals in utils/nutrition-progress).
// Events are inherently dated. No new storage: a pure read-time merge of
// clientGoals + events, same as the PRD's storage plan describes
// ("Milestones = merge en lectura de events + client-goals"). Returns every
// dated milestone regardless of date or completion - callers that only want
// the next upcoming one (resolveNextMilestone below) filter further.
//
// personalRecord is the exception to "regardless of completion": several
// can legitimately coexist (different exercises), so every one of those
// still passes through untouched. bodyWeight is a singleton by product
// design (see client-goals.js schema's own comment - "only one active goal
// at a time"), but none of the "Set Goal" entry points currently check for
// an existing one (even an expired one) before creating a new one, so
// clientGoals can end up holding more than one bodyWeight goal at a time in
// practice. Rather than writing a fix into those entry points - which would
// mean deciding whether to delete or complete the stale one, and marking it
// completed would misrepresent a goal that was simply abandoned, not
// actually reached - this collapses them down to a single one here, at read
// time, with the same precedence resolveActiveBodyweightGoal already uses
// elsewhere: the currently-active one wins; with none active (all expired,
// or the only one not started yet), the most recently started one wins, so
// there's still always exactly one shown.
export const resolveMilestones = (clientGoals, events) => {
  const todayUtc = Number(moment.utc().format("YYYYMMDD"));
  const bodyWeightGoals = (clientGoals || []).filter(
    (goal) => goal.type === "bodyWeight"
  );
  const activeBodyWeightGoal = bodyWeightGoals.find(
    (goal) =>
      goal.utcStartDate <= todayUtc &&
      (goal.utcTargetDate == null || goal.utcTargetDate >= todayUtc) &&
      !goal.completed
  );
  const currentBodyWeightGoalId =
    activeBodyWeightGoal?._id ??
    [...bodyWeightGoals].sort((a, b) => b.utcStartDate - a.utcStartDate)[0]
      ?._id;

  const goalMilestones = (clientGoals || [])
    .filter(
      (goal) =>
        (goal.type === "personalRecord" ||
          (goal.type === "bodyWeight" &&
            goal._id === currentBodyWeightGoalId)) &&
        goal.utcTargetDate != null &&
        !goal.hiddenFromPlanner
    )
    .map((goal) => ({
      id: goal._id,
      utcDate: goal.utcTargetDate,
      kind: goal.type,
      completed: !!goal.completed,
      label: goal.type === "personalRecord" ? goal.details.targetName : null,
      // Both optional on the schema (a coach can log a PR goal by name only) -
      // consumers fall back to the name-only label when either is missing.
      targetLoad:
        goal.type === "personalRecord"
          ? (goal.details.targetLoad ?? null)
          : null,
      targetReps:
        goal.type === "personalRecord"
          ? (goal.details.targetReps ?? null)
          : null,
      // Same field ClientGoals.js/nutrition-progress's analysis.js already
      // read off a bodyWeight goal (`bodyweightGoal.details.targetWeight`) -
      // carried through here so the grid's target-week pill can show it via
      // milestoneValueDetail below.
      targetWeight:
        goal.type === "bodyWeight" ? (goal.details.targetWeight ?? null) : null,
      // No color field on this schema - stays neutral, unlike event milestones below.
      color: null,
    }));

  // Events have no "completed" concept (the PRD is explicit: "todos los
  // futuros califican") - always false so the shape matches goalMilestones.
  // Unlike bodyWeight/personalRecord goals (no color field on that schema),
  // events already carry a coach-chosen eventColor - carried through here so
  // milestone chips can render it, while the other two kinds stay neutral.
  const eventMilestones = (events || []).map((event) => ({
    id: event._id,
    utcDate: event.fromDate,
    kind: "event",
    completed: false,
    label: event.name,
    color: event.eventColor ?? null,
  }));

  return [...goalMilestones, ...eventMilestones].sort(
    (a, b) => a.utcDate - b.utcDate
  );
};

export const MILESTONE_ICONS = {
  personalRecord: "/img/new-icons/personal-record-ico.svg",
  bodyWeight: "/img/new-icons/bodyweight.svg",
  event: "/img/new-icons/calendar-ico.svg",
};

// Same label choice as usePhaseSummaryData's "next milestone" chip, minus
// the PR (load×reps) detail - too tight a column for that, it stays exclusive
// to the status chip. Shared by List view's Milestones column and the Weekly
// Notes panel's context chips.
export const milestoneLabel = (milestone, t) => {
  if (milestone.kind === "personalRecord") {
    return t("annualPlanner.milestonePr", { name: milestone.label });
  }
  if (milestone.kind === "bodyWeight") {
    return t("annualPlanner.milestoneBodyweightGoal");
  }
  return milestone.label;
};

// The target-week value pill (e.g. "100x3", "82.5 Kg") - split out of
// usePhaseSummaryData's Next Milestone chip (the first caller) so the
// Calendar grid's Milestone row can show the exact same number instead of
// re-deriving it. Same load conversion as ClientGoals.js's own PR display
// (getLoadAndUnit) and Nutrition Progress's bodyweight goal card
// (getBodywweightAndUnit), so no surface ever disagrees on the value shown.
// null when the milestone carries no target value (a PR logged by name only,
// or an event, which has no such concept).
export const milestoneValueDetail = (milestone, units, t) => {
  if (
    milestone.kind === "personalRecord" &&
    milestone.targetLoad != null &&
    milestone.targetReps != null
  ) {
    return t("annualPlanner.milestonePrDetail", {
      load: getLoadAndUnit(milestone.targetLoad, units).value,
      reps: milestone.targetReps,
    });
  }
  if (milestone.kind === "bodyWeight" && milestone.targetWeight != null) {
    return getBodywweightAndUnit(milestone.targetWeight, units);
  }
  return null;
};

// List view's Milestones column pill (MilestonePill) needs the label and
// target value in one string ("Bodyweight goal · 90 Kg", "PR · Squat ·
// 100x3") - built from the same target-value fields milestoneValueDetail
// already derives, just without that helper's parenthesized PR formatting
// (meant for a label/detail two-line layout elsewhere, not an inline
// dot-joined one). Events have no target value, so they fall through to the
// plain label unchanged.
export const milestonePillLabel = (milestone, units, t) => {
  const label = milestoneLabel(milestone, t);
  if (
    milestone.kind === "personalRecord" &&
    milestone.targetLoad != null &&
    milestone.targetReps != null
  ) {
    const value = t("annualPlanner.milestonePrValue", {
      load: getLoadAndUnit(milestone.targetLoad, units).value,
      reps: milestone.targetReps,
    });
    return `${label} · ${value}`;
  }
  if (milestone.kind === "bodyWeight" && milestone.targetWeight != null) {
    return `${label} · ${getBodywweightAndUnit(milestone.targetWeight, units)}`;
  }
  return label;
};

// The week's phase attachment only carries a phaseName (see toLegacyShape's
// datesAttachments) - the color lives on the separate phase definition list,
// joined here by name. Shared by List view's phase pills and the Weekly
// Notes panel's context chips.
export const resolvePhaseColor = (phaseName, phasesList) =>
  (phasesList || []).find((phase) => phase.phaseName === phaseName)?.phaseColor;

export const resolveNextMilestone = (clientGoals, events, todayUtc) => {
  // resolveMilestones is sorted ascending, so the first match is the
  // earliest upcoming one.
  const next = resolveMilestones(clientGoals, events).find(
    (milestone) => !milestone.completed && milestone.utcDate >= todayUtc
  );

  return next ?? null;
};

// Custom set interval, used to blink the event
// It passes whatever arguments are required to the callback
const formatCompactThousands = (value) => {
  if (value > 10000) return `${Math.trunc(value / 1000)}k`;
  if (value > 1000) return `${Math.round((value / 1000) * 10) / 10}k`;
  return value;
};

export const getWeeklyStatsAvg = (
  weekStats,
  weekVolumes,
  previousWeekStats = [],
  units = "metric"
) => {
  const readiness = [];
  const sessionRPE = [];
  const bodyWeight = [];
  const kCal = [];
  const steps = [];
  const sleep = [];

  // The weekly volumes are just a sum and not an average
  let volumes =
    weekVolumes &&
    weekVolumes.reduce((acc, current) => acc + current.volume, 0);

  // We iterate the array of the current week stats
  weekStats.forEach((stat) => {
    // If the stat has readiness, we sum all the fields of the readiness and push it to the readiness array.
    // We do the same with each stat object of the week, and at the end we divide the result by the readiness array length
    stat.readiness &&
      readiness.push(
        Object.entries(stat.readiness).reduce(
          (acc, [key, value]) => acc + value,
          0
        )
      );

    // If the stat has sessionRPE info, we push it to the array
    stat.sessionRPE && sessionRPE.push(stat.sessionRPE);
    // If the stat has body weight info, we push it to the array
    stat.bodyWeight && bodyWeight.push(stat.bodyWeight);

    // If the stat has nutrients info, we calculate the kCal amount, and push it to the array
    if (stat.nutrients) {
      const proteinsKcal = stat.nutrients.proteins
        ? Math.round(stat.nutrients.proteins * 4)
        : 0;
      const carbsKcal = stat.nutrients.carbs
        ? Math.round(stat.nutrients.carbs * 4)
        : 0;
      const fatsKcal = stat.nutrients.fats
        ? Math.round(stat.nutrients.fats * 9)
        : 0;

      const calories = Math.round(proteinsKcal + carbsKcal + fatsKcal);
      kCal.push(calories);
    }
    // Also if the stat has steps, we push it to the array
    stat.steps && steps.push(stat.steps);

    // For when sleep is implemented, for now it won't push anything
    stat.sleep && sleep.push(stat.sleep);
  });

  // Once we iterated over all the current week stats, we divide the sums by the lenght of the arrays
  // and get the avg of the week. Same with all, except the volume which is just a sum

  const readinessAvg = readiness.length
    ? Math.round(
        readiness.reduce((acc, value) => acc + value) / readiness.length
      )
    : 0;

  // 1 decimal, matching Nutrition Progress's own RPE display convention
  // (TableFormat.js's displayRpe) rather than the whole-number rounding
  // every other stat here uses.
  const sessionRPEAvg = sessionRPE.length
    ? Number(
        (
          sessionRPE.reduce((acc, value) => acc + value) / sessionRPE.length
        ).toFixed(1)
      )
    : 0;

  // Same kg -> displayed-units conversion as getBodywweightAndUnit/
  // getLoadAndUnit elsewhere, instead of this function's own hardcoded
  // 2.205 multiplier.
  const toUserWeight = (kg) =>
    units === "metric" ? kg : convertUnit(kg, "kg", "lb");

  const bodyWeightAvg = bodyWeight.length
    ? Math.round(
        toUserWeight(
          bodyWeight.reduce((acc, value) => acc + value) / bodyWeight.length
        )
      )
    : 0;

  let bodyWeightDiff = 0;

  if (previousWeekStats.length) {
    const previousWeight = [];

    previousWeekStats.forEach((stat) => {
      stat.bodyWeight && previousWeight.push(stat.bodyWeight);
    });

    const previousWeightAvg = previousWeight.length
      ? Math.round(
          toUserWeight(
            previousWeight.reduce((acc, value) => acc + value) /
              previousWeight.length
          )
        )
      : 0;

    bodyWeightDiff = previousWeight
      ? Math.round(bodyWeightAvg - previousWeightAvg)
      : 0;
  }

  let kCalAvg = kCal.length
    ? Math.round(kCal.reduce((acc, value) => acc + value) / kCal.length)
    : 0;

  let stepsAvg = steps.length
    ? Math.round(steps.reduce((acc, value) => acc + value) / steps.length)
    : 0;

  const sleepAvg = sleep.length
    ? Math.round(sleep.reduce((acc, value) => acc + value) / sleep.length)
    : 0;

  // Same formatter the training calendar itself uses for weekly volume (see
  // volume-helpers.js's own comment) - Tracked Results' Weekly Volume stat
  // should read identically to volume everywhere else in the app, per the
  // lead's explicit ask, not its own rounding/no-unit-suffix convention.
  volumes = formatVolume(volumes, units);
  stepsAvg = formatCompactThousands(stepsAvg);
  kCalAvg = formatCompactThousands(kCalAvg);

  return {
    volumes,
    readinessAvg,
    sessionRPEAvg,
    bodyWeightAvg,
    kCalAvg,
    stepsAvg,
    sleepAvg,
    bodyWeightDiff,
  };
};

// Tracked Results' Weekly Volume/Bodyweight rows can render a value wider
// than their row's own fixed-size box (e.g. "33.9k" once a week's volume
// crosses 1000, via formatVolume above). Rather than growing the box (see
// [[project_periodization_wide_stats_logic]] for that earlier approach,
// kept for reference), the fix here is to shrink Tracked Results' own
// font-size/line-height instead - the box stays the fixed size everywhere
// else in the planner, only these 4 stat spans get smaller text. The
// border-cutting bug this guards against only ever happens on the CURRENT
// week specifically - every other week's box has no .current-week border
// drawn over it, so a wide value elsewhere just overflows quietly with
// nothing to visually cut through it. Scoped to that one week (not every
// week this calendar shows) so the smaller text only kicks in on the rare
// occasion it's actually needed. Reuses getWeeklyStatsAvg itself (not a
// separate leaner reimplementation) so this can never disagree with what
// Tracked Results actually renders.
export const hasWideWeeklyStatValue = (
  monthsData,
  startingCalendarDay,
  athleteStatsState,
  athleteVolumesState,
  units
) => {
  let currentWeekFirstDay = null;
  for (const { month, numberOfWeeks } of monthsData) {
    for (let weekNumber = 0; weekNumber < numberOfWeeks; weekNumber++) {
      const weekFirstDay = getWeekColumnData(
        month,
        startingCalendarDay,
        weekNumber
      );
      if (isCurrentWeek(weekFirstDay)) {
        currentWeekFirstDay = weekFirstDay;
        break;
      }
    }
    if (currentWeekFirstDay) break;
  }

  // Not in the visible range at all (e.g. the coach scrolled to a past/
  // future year) - no current-week border exists anywhere to cut through.
  if (!currentWeekFirstDay) return false;

  const weekFrom = Number(currentWeekFirstDay.format("YYYYMMDD"));
  const weekTo = Number(
    moment(currentWeekFirstDay).add(6, "days").format("YYYYMMDD")
  );

  const weekVolumes = (athleteVolumesState || []).filter(
    (volume) => volume.utcDate >= weekFrom && volume.utcDate <= weekTo
  );
  const weekStats = (athleteStatsState || []).filter(
    (stat) => stat.utcDate >= weekFrom && stat.utcDate <= weekTo
  );

  const { volumes, bodyWeightAvg } = getWeeklyStatsAvg(
    weekStats,
    weekVolumes,
    [],
    units
  );

  // formatVolume only ever abbreviates with a decimal once the whole part
  // needs 2+ digits (e.g. "33.9k") - "2.1k" (4 chars) is still a single
  // leading digit and fits the default box fine; 5+ chars is where it
  // actually starts overflowing.
  const displayVolume = volumes?.replace(/ (kg|lb)$/, "");
  if (displayVolume && displayVolume.length >= 5) return true;
  if (bodyWeightAvg && String(bodyWeightAvg).length >= 4) return true;

  return false;
};
